import { Modal } from "@/components";
import clsx from "clsx";
import { I18N_LANGUAGES } from "@/i18n";
import { useIntl } from "react-intl";
import { IClubResourceData } from "@/types/club-resource.types";
import { useSnackbar } from "notistack";
import { useTranslateClubResourceContentMutation } from "@/services/ClubResourceService";
import { ModalProps } from "@mui/base/Modal";
import { useEffect, useState } from "react";

interface ClubResourcePreviewModalProps extends Omit<ModalProps, "children"> {
  formData: Partial<IClubResourceData>;
  source_language?: string;
  onTranslationsGenerated?: (
    translations: Record<string, TranslationEntry>,
  ) => void;
}

interface TranslationEntry {
  description?: string;
}

interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  formData: Partial<IClubResourceData>;
  translatedContent?: Record<string, TranslationEntry>;
  isEditable?: boolean;
  onEditContent?: (languageCode: string, value: string) => void;
}

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  formData,
  translatedContent,
  isEditable = true,
  onEditContent,
}) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState<string>("");

  const langContent = translatedContent?.[language.code] ?? {
    description: formData.description,
  };

  useEffect(() => {
    setDescription(langContent.description || "");
  }, [langContent.description]);

  const handleToggleEdit = () => {
    if (!isEditable) return;
    setIsEditing((prev) => !prev);
  };

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <div>
            <p className="font-semibold text-gray-900 text-sm">
              {language.label}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {language.code.toUpperCase()}
            </p>
          </div>
        </div>

        {isEditable && (
          <button
            type="button"
            onClick={handleToggleEdit}
            className={clsx(
              "btn btn-xs rounded-full px-3 border-none",
              isEditing
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300",
            )}
          >
            {isEditing
              ? intl.formatMessage({ id: "COMMON.SAVE" })
              : intl.formatMessage({ id: "COMMON.EDIT" })}
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        <div className="mb-3">
          <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
            {intl.formatMessage({ id: "COMMON.NAME" })}
          </p>
          <p className="text-sm font-semibold text-gray-900">
            {formData.name || "-"}
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
            {intl.formatMessage({ id: "COMMON.DESCRIPTION" })}
          </p>
          {isEditing ? (
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (onEditContent) {
                  onEditContent(language.code, e.target.value);
                }
              }}
              className="w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[80px]"
            />
          ) : (
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {description || formData.description || "-"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const ClubResourcePreviewModal: React.FC<ClubResourcePreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  onTranslationsGenerated,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [translateClubResourceContent] =
    useTranslateClubResourceContentMutation();
  const [translations, setTranslations] = useState<
    Record<string, TranslationEntry>
  >({});
  const [hasTranslated, setHasTranslated] = useState(false);
  const [contentModified, setContentModified] = useState(false);
  const [lastDescription, setLastDescription] = useState<string>("");

  useEffect(() => {
    if (!formData.description) return;
    if (formData.description !== lastDescription) {
      setLastDescription(formData.description);
      setContentModified(true);
      setHasTranslated(false);
      setTranslations({});
    }
  }, [formData.description, lastDescription]);

  useEffect(() => {
    const fetchTranslations = async () => {
      if (!open) return;

      if (hasTranslated) {
        if (onTranslationsGenerated) {
          onTranslationsGenerated(translations);
        }
        return;
      }

      try {
        const payload = {
          description: formData.description,
          source_language: source_language || "en",
          resource_id:
            formData.id && !contentModified ? String(formData.id) : undefined,
        };

        const response = await translateClubResourceContent(payload).unwrap();

        if (response.translations) {
          const languageMap = response.translations as Record<
            string,
            TranslationEntry
          >;
          const normalized: Record<string, TranslationEntry> =
            Object.fromEntries(
              Object.entries(languageMap).map(([k, v]) => [
                k.toLowerCase(),
                v || {},
              ]),
            );
          // Mark translated before updating state to avoid effect re-runs
          setHasTranslated(true);
          setTranslations(normalized);

          if (onTranslationsGenerated) {
            onTranslationsGenerated(normalized);
          }
        }
      } catch (error) {
        // Log the error for debugging while still showing a user-friendly message
        console.error("Club resource translation failed:", error);
        enqueueSnackbar(
          intl.formatMessage({ id: "CLUB_RESOURCE.PREVIEW.TRANSLATION_ERROR" }),
          {
            variant: "error",
          },
        );
      }
    };

    fetchTranslations();
  }, [
    open,
    formData.description,
    formData.id,
    source_language,
    translateClubResourceContent,
    enqueueSnackbar,
    intl,
    hasTranslated,
    contentModified,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        "harbor-preview-root !flex items-center justify-center",
        className,
      )}
    >
      <div className="bg-gray-100 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {intl.formatMessage({ id: "CLUB_RESOURCE.PREVIEW.TITLE" })}
            </h2>
            <p className="text-sm text-gray-600 max-w-2xl">
              {formData.name || "-"}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 flex-1 overflow-y-auto">
          <div className="lang-list pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                formData={formData}
                translatedContent={translations}
                isEditable={lang.code !== (source_language || "en")}
                onEditContent={(languageCode, value) => {
                  setTranslations((prev) => {
                    const next = { ...prev };
                    const current = next[languageCode] || {};
                    next[languageCode] = {
                      ...current,
                      description: value,
                    };

                    if (onTranslationsGenerated) {
                      onTranslationsGenerated(next);
                    }

                    return next;
                  });
                }}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-100 border-t border-gray-100">
          <div className="footer-actions-row flex justify-end">
            <button
              type="button"
              onClick={() => onClose?.({}, "backdropClick")}
              className="btn btn-sm btn-ghost text-gray-700 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {intl.formatMessage({ id: "CLUB_RESOURCE.BUTTON.CLOSE" })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ClubResourcePreviewModal;
